import { useState } from 'react'
import { router } from '@inertiajs/react'
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { FormSelect } from '@/components/ui/form'

// ─── Types ──────────────────────────────────────

interface Totals {
  product_total: number
  commission: number
  freight: number
  total_invoice: number
  received_cost: number
  sum_payments: number
  sum_credit_memos: number
  balance: number
  currency: string | null
}

interface Payment {
  id: number
  amount: number
  date: string | null
  company_id: number | null
  company_name: string | null
  form_of_payment_id: number | null
  form_of_payment_name: string | null
}

interface CreditMemo {
  id: number
  amount: number
  date: string | null
}

interface FinanceTabProps {
  vpoId: number
  totals: Totals
  payments: Payment[] | undefined
  creditMemos: CreditMemo[] | undefined
  companies: { id: number; name: string }[]
  paymentForms: { id: number; name: string }[]
  csrfToken: string
  onNotify: (msg: string) => void
}

// ─── Helpers ────────────────────────────────────

const currencySymbols: Record<string, string> = { usd: '$', eur: '€', gbp: '£' }
function fmtMoney(n: number, currency: string | null): string {
  const sym = currencySymbols[(currency || 'usd').toLowerCase()] || ''
  return `${sym}${n.toFixed(2)}`
}

// ─── FinanceTab ─────────────────────────────────

export default function FinanceTab({
  vpoId, totals, payments, creditMemos, companies, paymentForms, csrfToken, onNotify,
}: FinanceTabProps) {
  const [addingPayment, setAddingPayment] = useState(false)
  const [pAmount, setPAmount] = useState('')
  const [pDate, setPDate] = useState('')
  const [pCompany, setPCompany] = useState('')
  const [pForm, setPForm] = useState('')

  const [addingMemo, setAddingMemo] = useState(false)
  const [mAmount, setMAmount] = useState('')
  const [mDate, setMDate] = useState('')

  const reloadFinance = () => router.reload({ only: ['payments', 'credit_memos', 'totals'] })

  const submitPayment = async () => {
    if (!pAmount || !pDate) {
      onNotify('Amount and date are required.')
      return
    }
    const fd = new FormData()
    fd.append('amount', pAmount)
    fd.append('date', pDate)
    if (pCompany) fd.append('company', pCompany)
    if (pForm) fd.append('paymentForm', pForm)
    const res = await fetch(`/vpo/create_payment/${vpoId}`, {
      method: 'POST', body: fd, headers: { 'X-CSRF-Token': csrfToken },
    })
    if (res.ok) {
      onNotify('Payment added.')
      setPAmount(''); setPDate(''); setPCompany(''); setPForm(''); setAddingPayment(false)
      reloadFinance()
    } else {
      onNotify(`Failed to add payment (${res.status}).`)
    }
  }

  const deletePayment = async (id: number) => {
    if (!confirm('Delete this payment?')) return
    const res = await fetch(`/vpo/delete_payment/${id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
    })
    if (res.ok) { onNotify('Payment deleted.'); reloadFinance() }
    else onNotify(`Failed to delete payment (${res.status}).`)
  }

  const submitMemo = async () => {
    if (!mAmount || !mDate) {
      onNotify('Amount and date are required.')
      return
    }
    const fd = new FormData()
    fd.append('amount', mAmount)
    fd.append('date', mDate)
    const res = await fetch(`/vpo/create_credit_memo/${vpoId}`, {
      method: 'POST', body: fd, headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
    })
    if (res.ok || res.redirected) {
      onNotify('Credit memo added.')
      setMAmount(''); setMDate(''); setAddingMemo(false)
      reloadFinance()
    } else {
      onNotify(`Failed to add credit memo (${res.status}).`)
    }
  }

  const deleteMemo = async (id: number) => {
    if (!confirm('Delete this credit memo?')) return
    const res = await fetch(`/vpo/delete_credit_memo/${id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
    })
    if (res.ok) { onNotify('Credit memo deleted.'); reloadFinance() }
    else onNotify(`Failed to delete credit memo (${res.status}).`)
  }

  const cur = totals.currency
  return (
    <div className="space-y-6" data-testid="vpo-finance-tab">
      {/* ── Totals grid ── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ['Product Total', totals.product_total],
          ['Commission',    totals.commission],
          ['Freight',       totals.freight],
          ['Total Invoice', totals.total_invoice],
          ['Received Cost', totals.received_cost],
          ['Sum Payments',  totals.sum_payments],
          ['Credit Memos',  totals.sum_credit_memos],
          ['Balance',       totals.balance],
        ].map(([label, val]) => (
          <div key={String(label)} className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
            <div className="mt-1 text-base font-semibold tabular-nums text-zinc-900 dark:text-white">
              {fmtMoney(Number(val), cur)}
            </div>
          </div>
        ))}
      </div>

      {/* ── Payments ── */}
      <section data-testid="vpo-payments-section">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Payments to Vendor</h3>
          <button
            type="button"
            onClick={() => setAddingPayment(o => !o)}
            data-testid="vpo-add-payment-toggle"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <PlusIcon className="h-3 w-3" />
            {addingPayment ? 'Cancel' : 'Add Payment'}
          </button>
        </div>

        {addingPayment && (
          <div className="mb-3 grid grid-cols-1 gap-2 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3 dark:border-indigo-500/30 dark:bg-indigo-500/5 sm:grid-cols-5" data-testid="vpo-payment-form">
            <input type="number" step="any" placeholder="Amount *" value={pAmount} onChange={e => setPAmount(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
            <input type="date" value={pDate} onChange={e => setPDate(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
            <FormSelect value={pCompany} onChange={setPCompany} placeholder="Company"
              options={companies.map(c => ({ value: String(c.id), label: c.name }))} />
            <FormSelect value={pForm} onChange={setPForm} placeholder="Form"
              options={paymentForms.map(f => ({ value: String(f.id), label: f.name }))} />
            <button type="button" onClick={submitPayment} data-testid="vpo-payment-submit"
              className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
              Save
            </button>
          </div>
        )}

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-700">
              <th className="py-1.5 pr-2">Date</th>
              <th className="py-1.5 pr-2">Amount</th>
              <th className="py-1.5 pr-2">Company</th>
              <th className="py-1.5 pr-2">Form</th>
              <th className="py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {payments === undefined ? (
              <tr><td colSpan={5} className="py-2 text-xs text-zinc-400" data-testid="vpo-payments-loading">Loading payments…</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan={5} className="py-2 text-xs text-zinc-400">No payments recorded.</td></tr>
            ) : (
              payments.map(p => (
                <tr key={p.id} className="group border-b border-zinc-100 dark:border-zinc-800">
                  <td className="py-1.5 pr-2 text-zinc-600 dark:text-zinc-400">{p.date || '—'}</td>
                  <td className="py-1.5 pr-2 font-medium tabular-nums">{fmtMoney(p.amount, cur)}</td>
                  <td className="py-1.5 pr-2">{p.company_name || '—'}</td>
                  <td className="py-1.5 pr-2">{p.form_of_payment_name || '—'}</td>
                  <td className="py-1.5">
                    <button
                      onClick={() => deletePayment(p.id)}
                      data-testid={`vpo-payment-delete-${p.id}`}
                      className="hidden rounded-md border border-zinc-200 p-0.5 text-zinc-400 hover:border-red-200 hover:text-red-500 group-hover:inline dark:border-zinc-700"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {/* ── Credit Memos ── */}
      <section data-testid="vpo-credit-memos-section">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Vendor Credit Memos</h3>
          <button
            type="button"
            onClick={() => setAddingMemo(o => !o)}
            data-testid="vpo-add-memo-toggle"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <PlusIcon className="h-3 w-3" />
            {addingMemo ? 'Cancel' : 'Add Credit Memo'}
          </button>
        </div>

        {addingMemo && (
          <div className="mb-3 grid grid-cols-1 gap-2 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3 dark:border-indigo-500/30 dark:bg-indigo-500/5 sm:grid-cols-3" data-testid="vpo-memo-form">
            <input type="number" step="any" placeholder="Amount *" value={mAmount} onChange={e => setMAmount(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
            <input type="date" value={mDate} onChange={e => setMDate(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
            <button type="button" onClick={submitMemo} data-testid="vpo-memo-submit"
              className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
              Save
            </button>
          </div>
        )}

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-700">
              <th className="py-1.5 pr-2">Date</th>
              <th className="py-1.5 pr-2">Amount</th>
              <th className="py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {creditMemos === undefined ? (
              <tr><td colSpan={3} className="py-2 text-xs text-zinc-400" data-testid="vpo-memos-loading">Loading credit memos…</td></tr>
            ) : creditMemos.length === 0 ? (
              <tr><td colSpan={3} className="py-2 text-xs text-zinc-400">No credit memos recorded.</td></tr>
            ) : (
              creditMemos.map(m => (
                <tr key={m.id} className="group border-b border-zinc-100 dark:border-zinc-800">
                  <td className="py-1.5 pr-2 text-zinc-600 dark:text-zinc-400">{m.date || '—'}</td>
                  <td className="py-1.5 pr-2 font-medium tabular-nums">{fmtMoney(m.amount, cur)}</td>
                  <td className="py-1.5">
                    <button
                      onClick={() => deleteMemo(m.id)}
                      data-testid={`vpo-memo-delete-${m.id}`}
                      className="hidden rounded-md border border-zinc-200 p-0.5 text-zinc-400 hover:border-red-200 hover:text-red-500 group-hover:inline dark:border-zinc-700"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  )
}
